<template>
  <div class="box">
    <div>
      <IssueArticleHeader
        :article="article"
        :publication-name="publicationName"
        :cover-display-date="coverDisplayDate"
        :xml-summary="xml_summary"
        :xml-byline="xml_byline"
        :word-count="articleWordCount"
      />
      <IssueArticlePrimaryImage
        v-if="primaryImage"
        :primary_image="primaryImage"
      />

      <div v-if="hasUdfBody" class="issue-article-body issue-article-body--udf">
        <div
          v-for="(udfBody, index) in udfBodyList"
          :key="index"
        >
          <IssueArticleContent
            :article-body="udfBody"
          />
        </div>
      </div>
      <div v-if="hasXml" class="issue-article-body issue-article-body--xml">
        <div v-html="xml_content" />
      </div>
    </div>
  </div>
</template>

<script>
import IssueArticleHeader from '@/components/layout/issue/article/IssueArticleHeader.vue'
import IssueArticleContent from '@/components/layout/issue/article/IssueArticleContent.vue'
import IssueArticlePrimaryImage from '@/components/layout/issue/article/IssueArticlePrimaryImage.vue'
import { countWordsFromUdfBody } from '@/utils/articleWordCount'
import { parsePrintDocumentArticleXml, countWordsFromPrintDocumentXml } from '@/utils/printDocumentXml'

export default {
  name: 'IssueArticleDetails',
  components: {
    IssueArticleHeader,
    IssueArticleContent,
    IssueArticlePrimaryImage
  },
  props: {
    article: {
      type: Object,
      required: true
    },
    publicationName: {
      type: String,
      required: true
    },
    coverDisplayDate: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      xml_summary: '',
      xml_content: '',
      xml_byline: ''
    }
  },
  computed: {
    printData () {
      return this.article?.printDocuments?.[0] ?? null
    },
    hasUdfBody () {
      const body = this.printData?.udf?.body
      return Array.isArray(body) && body.length > 0
    },
    hasXml () {
      return !!this.printData?.xml
    },
    primaryImage () {
      return this.printData?.udf?.primary_image ?? null
    },
    udfBodyList () {
      const body = this.printData?.udf?.body
      return Array.isArray(body) ? body : []
    },
    /** Source-only count: UDF paragraph/blockquote text, else XML body `p`/`h1` (no captions, credits, or image blocks). */
    articleWordCount () {
      const data = this.printData
      if (!data) return null
      if (this.hasUdfBody) {
        return countWordsFromUdfBody(data.udf.body)
      }
      if (data.xml) {
        return countWordsFromPrintDocumentXml(data.xml)
      }
      return null
    }
  },
  watch: {
    article: {
      handler: 'syncArticlePresentation',
      deep: true
    }
  },
  mounted () {
    this.syncArticlePresentation()
  },
  methods: {
    syncArticlePresentation () {
      this.applyPrintDocumentState()
    },
    applyPrintDocumentState () {
      const data = this.printData
      if (!data) {
        this.clearXmlFields()
        this.$store.commit('setStoreXmlByline', '')
        return
      }

      if (!data.xml) {
        this.clearXmlFields()
        this.$store.commit('setStoreXmlByline', '')
        return
      }

      const parsed = parsePrintDocumentArticleXml(data.xml)
      this.xml_summary = parsed.xmlSummary || ''
      this.xml_content = parsed.xmlContent || ''
      this.xml_byline = parsed.xmlByline || ''
      this.$store.commit('setStoreXmlByline', this.xml_byline)
    },
    clearXmlFields () {
      this.xml_summary = ''
      this.xml_content = ''
      this.xml_byline = ''
    }
  }
}
</script>

<style scoped>
  .box {
    min-width: 0;
    max-width: 100%;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .issue-article-body :deep(img),
  .issue-article-body :deep(video),
  .issue-article-body :deep(iframe) {
    max-width: 100%;
    height: auto;
  }

  .issue-article-body :deep(table) {
    max-width: 100%;
    display: block;
    overflow-x: auto;
  }
</style>
